import StageProgress from 'flow/components/guide/StageProgress';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import useAppContext from 'flow/hooks/useAppContext';
import { Statistics } from 'flow/types/Channel';
import { PbxParams } from 'flow/types/Params';
import { QueueAndChannelStats } from 'flow/types/Queue';
import { routes } from 'flow/types/routes';
import { TypedMap } from 'flow/types/TypedMap';
import { isChannelKey, isQueueKey } from 'flow/utils';
import { getBgColorFromKey, getTextColorFromKey } from 'flow/utils/pbx-utils';
import { getServiceLevelsForQueue } from 'flow/utils/servicelevel-utils';
import { Map } from 'immutable';
import React, { MouseEventHandler, useEffect, useState } from 'react';
import { generatePath, NavLink, useParams } from 'react-router-dom';
import PercentageWidget from './statistic-widgets/PercentageWidget';
import TimeWidget from './statistic-widgets/TimeWidget';

interface Props {
    getStatisticsFavorites: (entityKey: string) => string[];
    statSummary: TypedMap<QueueAndChannelStats> | TypedMap<Statistics>;
    handleStatisticsIntervalChange?: (intervalResolution: string) => void;
    current: number;
    setCurrent: (value: number) => void;
}

const intervalHeaders = [
    'Queue.Statistics.Interval.Today',
    'Queue.Statistics.Interval.Week',
    'Queue.Statistics.Interval.Month',
];
const intervals = ['today', 'week', 'month'];

const AgentStatisticsPreview = ({
    statSummary,
    getStatisticsFavorites,
    handleStatisticsIntervalChange,
    current,
    setCurrent,
}: Props) => {
    const { i18next } = useAppContext();
    const { entityKey } = useParams<PbxParams>();

    useEffect(() => {
        setCurrent(0);
    }, [entityKey, setCurrent]);

    let stats: string,
        answered: string,
        received: string,
        strokeColor: string,
        statisticRoute: string;
    if (isQueueKey(entityKey)) {
        stats = 'queueStats';
        answered = 'answeredCalls';
        received = 'recievedCalls'; // Typo from API
        strokeColor = getComputedStyle(
            document.documentElement,
        ).getPropertyValue('--color-huntgroup');
        statisticRoute = generatePath(
            routes['pbx.queues.entityKey.statistics'],
            {
                entityKey,
            },
        );
    } else if (isChannelKey(entityKey)) {
        stats = 'channelStats';
        answered = 'answeredChats';
        received = 'initializedChats';
        strokeColor = getComputedStyle(
            document.documentElement,
        ).getPropertyValue('--color-channel');
        statisticRoute = generatePath(
            routes['pbx.channels.entityKey.statistics'],
            {
                entityKey,
            },
        );
    }

    const trailColor = getComputedStyle(
        document.documentElement,
    ).getPropertyValue('--color-ornament');

    const statisticsIntervalChange = (intervalIndex: number) => {
        setCurrent(intervalIndex);

        if (handleStatisticsIntervalChange) {
            const intervalResolution = intervals[intervalIndex];
            handleStatisticsIntervalChange(intervalResolution);
        }
    };

    const handleForward: MouseEventHandler = (event) => {
        event.preventDefault();

        const newIndex = current + 1;
        setCurrent(newIndex);

        const newIntervalIndex = Math.min(newIndex, intervals.length);
        statisticsIntervalChange(newIntervalIndex);
    };

    const handleBack: MouseEventHandler = (event) => {
        event.preventDefault();

        const newIndex = current - 1;
        setCurrent(newIndex);

        const newIntervalIndex = Math.max(newIndex, 0);
        statisticsIntervalChange(newIntervalIndex);
    };

    const renderStatisticsIcon = (index: number, statistic: string) => {
        const serviceLevels = getServiceLevelsForQueue(entityKey);

        return (() => {
            switch (statistic) {
                case answered: {
                    const intervalStatistics = statSummary.getIn(
                        [stats, intervals[index]],
                        Map(),
                    );

                    return (
                        <>
                            <div className="text-sm font-bold text-gray-800">
                                {i18next.t(
                                    'Queue.Statistics.AnswerRatio.Title',
                                )}
                            </div>
                            <div>
                                <PercentageWidget
                                    successfulEvents={intervalStatistics.get(
                                        answered,
                                    )}
                                    allEvents={intervalStatistics.get(received)}
                                    strokeColor={strokeColor}
                                    trailColor={trailColor}
                                />
                            </div>
                        </>
                    );
                }

                case 'avgWaitTime': {
                    const intervalStatistics = statSummary.getIn(
                        [stats, intervals[index]],
                        Map(),
                    );

                    return (
                        <>
                            <div className="text-sm font-bold text-gray-800">
                                {i18next.t(
                                    'Queue.Statistics.AverageWaitTime.Title',
                                )}
                            </div>
                            <div>
                                <TimeWidget
                                    timeInSeconds={intervalStatistics.get(
                                        'avgWaitTime',
                                    )}
                                />
                            </div>
                        </>
                    );
                }
                case 'avgTalkTime': {
                    const intervalStatistics = statSummary.getIn(
                        [stats, intervals[index]],
                        Map(),
                    );

                    return (
                        <>
                            <div className="text-sm font-bold text-gray-800">
                                {i18next.t(
                                    'Queue.Statistics.AverageTalkTime.Title',
                                )}
                            </div>
                            <div>
                                <TimeWidget
                                    timeInSeconds={intervalStatistics.get(
                                        'avgTalkTime',
                                    )}
                                />
                            </div>
                        </>
                    );
                }

                case 'serviceLevels60':
                case 'serviceLevels120': {
                    // Same goes for serviceLevels120 below.
                    // labels.
                    // So keep in mind that we have to migrate old settings if we want to change these
                    // position anymore => Favorite statistics widgets are broken!
                    // it is there anyway which means it will not be possilbe to set a widget at that
                    // favorite it will no longer show up as a statistics widget but the app will think
                    // Just changing the name will result in that if a user has this old value as a
                    // widgets still work.
                    // settings so customers don't lose their settings and so the favorite statistics
                    // but we kept is since it is a key stored in localStorage for statistics widget
                    // serviceLevels60 is not the best name now when we can customize service levels

                    const level = statistic === 'serviceLevels60' ? 0 : 1;
                    const duration = statistic === 'serviceLevels60' ? 1 : 2;

                    const intervalStatistics = statSummary.getIn(
                        [stats, intervals[index]],
                        Map(),
                    );
                    const serviceLevel = intervalStatistics.getIn([
                        'serviceLevels',
                        `${serviceLevels[level]}`,
                    ]);

                    return (
                        <>
                            <div className="flex items-center gap-1 text-sm">
                                <p className="font-bold text-gray-800">
                                    {i18next.t(
                                        'Queue.Statistics.ServiceLevel.Title',
                                    )}
                                </p>

                                <p className="text-gray-500">
                                    {duration} {i18next.t('duration.min')}
                                </p>
                            </div>

                            <div>
                                <PercentageWidget
                                    successfulEvents={serviceLevel}
                                    allEvents={intervalStatistics.get(received)}
                                    strokeColor={strokeColor}
                                    trailColor={trailColor}
                                />
                            </div>
                        </>
                    );
                }

                default: {
                    return (
                        <>
                            <div className="text-sm font-bold text-gray-800">
                                {i18next.t(
                                    'Queue.Statistics.Widget.Empty.Title',
                                )}
                            </div>
                            <div className="my-4 flex items-center justify-center">
                                {/* Decorative empty-state art, not an action. */}
                                <span className="flex items-center justify-center p-1">
                                    <Icon
                                        iconKey="controlPoint"
                                        size="3.75rem"
                                        className={getTextColorFromKey(
                                            entityKey,
                                        )}
                                    />
                                </span>
                            </div>
                        </>
                    );
                }
            }
        })();
    };

    const favorites = getStatisticsFavorites(entityKey);
    const answer = favorites ? favorites[0] : answered;
    const firstWidget = renderStatisticsIcon(current, answer);
    const secondWidget = favorites
        ? renderStatisticsIcon(current, favorites[1])
        : renderStatisticsIcon(current, '');

    return (
        <div className="flex w-full flex-col bg-gray-100 p-3">
            <NavLink
                className="flex h-64 w-full max-w-96 flex-col self-center rounded-md border bg-white p-6 dark:bg-gray-200"
                to={statisticRoute}
            >
                <div className="flex w-full flex-col gap-2">
                    <div className="flex w-full items-center justify-between">
                        {current > 0 ? (
                            <IconButton
                                onClick={handleBack}
                                aria-label={i18next.t('action.previous')}
                            >
                                <Icon iconKey="arrow_back" />
                            </IconButton>
                        ) : (
                            <div className="size-8" />
                        )}
                        <div>
                            <div className="bg-green flex rounded-md p-1 text-xs text-white">
                                {i18next.t(intervalHeaders[current])}
                            </div>
                        </div>
                        {current + 1 !== intervals.length ? (
                            <IconButton
                                onClick={handleForward}
                                aria-label={i18next.t('action.next')}
                            >
                                <Icon iconKey="arrow_forward" />
                            </IconButton>
                        ) : (
                            <div className="size-8" />
                        )}
                    </div>
                    <div className="flex w-full justify-between">
                        <div className="flex size-40 flex-col items-center gap-y-3 p-2">
                            {firstWidget}
                        </div>
                        <div className="flex size-40 flex-col items-center gap-y-3 p-2">
                            {secondWidget}
                        </div>
                    </div>
                </div>
            </NavLink>

            <div className="flex items-center justify-center p-2">
                <StageProgress
                    stages={intervals.length}
                    currentStage={current}
                    handleSelect={(index) => statisticsIntervalChange(index)}
                    selectedColor={getBgColorFromKey(entityKey)}
                />
            </div>
        </div>
    );
};

export default AgentStatisticsPreview;
